import React, { createContext, useContext, useState, ReactNode, useMemo, useCallback } from "react";
import { randomIdGenerator } from "@utils/random-id-generator.util";
import { Toast } from "@components/toast";
import "./toast.scss";

interface ToastProviderProps {
  children: ReactNode;
}

interface ToastContextType {
  showToast: (toast: ToastType) => void;
}

export interface ToastType {
  title: string;
  content: ReactNode;
  duration?: number;
  id?: string;
  destroy?: (toastId: string) => void;
  disableAutoClose?: boolean;
  variant: "success" | "danger";
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastType[]>([]);

  const showToast = useCallback((newToast: ToastType) => {
    setToasts((prevToasts) => [
      ...prevToasts,
      { ...newToast, id: newToast.id || randomIdGenerator() },
    ]);
  }, []);

  const destroyToast = useCallback((toastId: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== toastId));
  }, []);

  const contextValue: ToastContextType = useMemo(() => {
    return {
      showToast,
      destroyToast,
    };
  }, [destroyToast, showToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className="toasts-wrapper">
        {toasts.map(({ id, title, content, duration, variant, disableAutoClose }) => {
          return (
            <Toast
              key={id}
              id={id as string}
              title={title}
              content={content}
              duration={duration}
              variant={variant}
              destroy={destroyToast}
              disableAutoClose={disableAutoClose}
            />
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

const useToastContext = (): ToastContextType => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToastContext must be used within a ToastProvider");
  }

  return context;
};

export { useToastContext, ToastProvider };
